import * as React from 'react';
import { mergeClasses } from '@griffel/react';
import { useIconState } from './useIconState.js';
import { useRootStyles } from './createFluentIcon.styles.js';
import { iconClassName } from './constants.js';
import { computeViewBox, createColorChildrenResolver, renderSvgBody } from '../core/svg.js';
/**
 * Creates a Fluent icon React component with Griffel styling.
 *
 * @param displayName - The display name for the component (used in React DevTools).
 * @param width - The intrinsic width/height of the icon (e.g. `"20"`, `"24"`, `"1em"`).
 * @param pathsOrSvg - Icon content in one of three forms:
 *   - `string[]` — Array of SVG path `d` attributes (mono-color icons).
 *   - `SvgNode[]` — Structured SVG element tree for color icons (CSP-safe).
 *   - `string` — Raw SVG innerHTML string.
 *     **Deprecated:** Use `SvgNode[]` with `options.color` instead. The `string` overload uses
 *     `dangerouslySetInnerHTML` which violates Trusted Types CSP policies.
 * @param options - Optional configuration.
 */
export const createFluentIcon = (displayName, width, pathsOrSvg, options) => {
    const viewBoxWidth = computeViewBox(width);
    // Resolve color children once per component: mono-color icons pay nothing,
    // color icons get per-instance memoized `idPrefix` scoping.
    const useColorChildren = createColorChildrenResolver(pathsOrSvg, options);
    const Icon = React.forwardRef((props, ref) => {
        const styles = useRootStyles();
        const iconState = useIconState(props, { flipInRtl: options === null || options === void 0 ? void 0 : options.flipInRtl });
        const colorChildren = useColorChildren(props.idPrefix);
        const state = {
            ...iconState,
            className: mergeClasses(iconClassName, iconState.className, styles.root),
            ref,
            width,
            height: width,
            viewBox: `0 0 ${viewBoxWidth} ${viewBoxWidth}`,
            xmlns: 'http://www.w3.org/2000/svg',
        };
        return renderSvgBody(state, pathsOrSvg, colorChildren);
    });
    Icon.displayName = displayName;
    return Icon;
};
